import {
  getUnknownParticipantId,
  isUnknownParticipantId,
  ParticipantTimesMap,
  ParticipantViewModel,
} from 'stageclip-common';

interface AegisubLineInfo {
  startTime: string;
  endTime: string;
  participantId: string;
}

function createAegisubName(participant: ParticipantViewModel) {
  return `${participant.fullName} # ${participant.subtitle} # ${participant.secondSubtitle} # ${participant.id}`.replace(
    /,/gi,
    '',
  );
}

function getParticipantIdFromData(info: string): string {
  if (info === '') {
    return getUnknownParticipantId();
  }

  const parts = info.split('#');

  return parts.pop().trim();
}

function parseAegisubLine(line: string): AegisubLineInfo {
  /*
    The aegisub data is formatted as follows:
    Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
  */
  const parts = line.split(',');

  if (parts.length < 10) {
    throw new Error('Invalid aegisub format');
  }
  const startIndex = 1;
  const endIndex = 2;
  const dataIndex = 9;
  const timestampRegex = /\d+:[0-5][0-9]:[0-5][0-9]\.\d{2}$/;
  const endTime = parts[endIndex].trim();
  const startTime = parts[startIndex].trim();

  if (!timestampRegex.test(startTime) || !timestampRegex.test(endTime)) {
    throw new Error('Invalid aegisub format');
  }

  return {
    endTime,
    participantId: getParticipantIdFromData(parts[dataIndex].trim()),
    startTime,
  };
}

function timeOrNull(time: string) {
  if (!time) {
    return '0:00:00.00';
  }

  return time;
}

export function getAegisubNamesFromParticipants(participants: ParticipantViewModel[]) {
  return participants.sort((a, b) => (a.eventOrderIndex < b.eventOrderIndex ? -1 : 1)).map(createAegisubName);
}

export function processAegisubResult(data: string): ParticipantTimesMap {
  const dataRows = data.split('\n');
  const times: ParticipantTimesMap = {};
  const defaultTimePrefix = '0:00:00';

  if (dataRows.length % 2 !== 0) {
    throw new Error('Invalid aegisub format - maybe an empty line at the end of the input?');
  }

  while (dataRows.length >= 2) {
    const audioRow = dataRows.shift();
    const videoRow = dataRows.shift();

    const audioData = parseAegisubLine(audioRow);
    const videoData = parseAegisubLine(videoRow);

    if (
      videoData.participantId &&
      !isUnknownParticipantId(videoData.participantId) &&
      videoData.participantId !== audioData.participantId
    ) {
      throw new Error('Invalid aegisub format');
    }

    if (
      audioData.endTime.startsWith(defaultTimePrefix) ||
      videoData.endTime.startsWith(defaultTimePrefix) ||
      audioData.startTime >= audioData.endTime ||
      videoData.startTime >= videoData.endTime
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    times[audioData.participantId] = {
      audioEndTime: audioData.endTime,
      audioStartTime: audioData.startTime,
      videoEndTime: videoData.endTime,
      videoStartTime: videoData.startTime,
    };
  }

  return times;
}

export function getAegisubProcessedDataFromParticipants(participants: ParticipantViewModel[]) {
  return participants
    .sort((a, b) => (a.audioStartTimestamp < b.audioStartTimestamp ? -1 : 1))
    .map(
      participant =>
        `Dialogue: 0,${timeOrNull(participant.audioStartTimestamp)},${timeOrNull(participant.audioEndTimestamp)},` +
        `Default,,0,0,0,,${createAegisubName(participant)}\n` +
        `Dialogue: 0,${timeOrNull(participant.clipStartTimestamp)},${timeOrNull(participant.clipEndTimestamp)},` +
        `Default,,0,0,0,,`,
    );
}
